import * as dexieDB from './helpers';
import * as firebaseDB from '../firebase/database';
import { syncPendingUploads } from '../../storage/FileUpload';

const syncInProgress = new Map();

export const syncDexieWithFirebase = async (userId) => {
    if (!userId || syncInProgress.get(userId)) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    syncInProgress.set(userId, true);
    try {
        await syncPendingUploads(userId);
        const permissionDate = await firebaseDB.getUserPermission(userId, () => { });
        if (permissionDate !== null) {
            await dexieDB.setUserPermission(userId, permissionDate);
        }

        const betaUser = await firebaseDB.getUserBeta(userId, () => { });
        await dexieDB.setUserBeta(userId, betaUser);

        const lastSync = await dexieDB.getLastSyncDate(userId);
        const dexieList = await dexieDB.getUserAnalysisList(userId, true) || {};
        let firebaseList = {};
        if (lastSync) {
            firebaseList = await firebaseDB.getUserAnalysisListSince(userId, lastSync) || {};
        } else {
            firebaseList = await firebaseDB.getUserAnalysisList(userId, true) || {};
        }

        const dexieChangedUids = Object.keys(dexieList || {}).filter(uid => {
            if (!lastSync) return true;
            const dexieDate = new Date(dexieList[uid].lastUpdate || 0);
            return dexieDate > new Date(lastSync);
        });

        const allUids = new Set([
            ...dexieChangedUids,
            ...Object.keys(firebaseList || {})
        ]);

        for (const uid of allUids) {
            const dexieAnalysis = dexieList[uid];
            const firebaseAnalysis = firebaseList[uid];

            if (dexieAnalysis && !firebaseAnalysis) {
                const dexieData = await dexieDB.getUserAnalysisData(userId, uid) || {};
                await firebaseDB.updateMultipleNodes(userId, uid, dexieAnalysis, dexieData);
                continue;
            }

            if (!dexieAnalysis && firebaseAnalysis) {
                const firebaseData = await firebaseDB.getUserAnalysisData(userId, uid) || {};
                await dexieDB.putUserAnalysis(userId, uid, firebaseAnalysis, firebaseData);
                continue;
            }

            if (dexieAnalysis && firebaseAnalysis) {
                if (dexieAnalysis.deleted && !firebaseAnalysis.deleted) {
                    const dexieData = await dexieDB.getUserAnalysisData(userId, uid) || {};
                    await firebaseDB.updateMultipleNodes(userId, uid, dexieAnalysis, dexieData);
                    continue;
                }

                if (!dexieAnalysis.deleted && firebaseAnalysis.deleted) {
                    const firebaseData = await firebaseDB.getUserAnalysisData(userId, uid) || {};
                    await dexieDB.putUserAnalysis(userId, uid, firebaseAnalysis, firebaseData);
                    continue;
                }
                const dexieDate = new Date(dexieAnalysis.lastUpdate || 0);
                const firebaseDate = new Date(firebaseAnalysis.lastUpdate || 0);

                if (dexieDate >= firebaseDate) {
                    const dexieData = await dexieDB.getUserAnalysisData(userId, uid) || {};
                    await firebaseDB.updateMultipleNodes(userId, uid, dexieAnalysis, dexieData);
                } else {
                    const firebaseData = await firebaseDB.getUserAnalysisData(userId, uid) || {};
                    await dexieDB.putUserAnalysis(userId, uid, firebaseAnalysis, firebaseData);
                }
            }
        }
    } finally {
        await dexieDB.setLastSyncDate(userId, new Date().toISOString());
        syncInProgress.delete(userId);
    }
};

export const syncAnalysisWithFirebase = async (userId, analysisUid) => {
    const key = `${userId}_${analysisUid}`;
    if (!userId || !analysisUid || syncInProgress.get(key)) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    syncInProgress.set(key, true);
    try {
        const dexieList = await dexieDB.getUserAnalysisList(userId, true) || {};
        const firebaseList = await firebaseDB.getUserAnalysisList(userId, true) || {};

        const dexieAnalysis = dexieList[analysisUid];
        const firebaseAnalysis = firebaseList[analysisUid];

        if (!dexieAnalysis && !firebaseAnalysis) return;

        if (dexieAnalysis && !firebaseAnalysis) {
            const dexieData = await dexieDB.getUserAnalysisData(userId, analysisUid) || {};
            await firebaseDB.updateMultipleNodes(userId, analysisUid, dexieAnalysis, dexieData);
            return;
        }

        if (!dexieAnalysis && firebaseAnalysis) {
            const firebaseData = await firebaseDB.getUserAnalysisData(userId, analysisUid) || {};
            await dexieDB.putUserAnalysis(userId, analysisUid, firebaseAnalysis, firebaseData);
            return;
        }

        if (dexieAnalysis.deleted && !firebaseAnalysis.deleted) {
            const dexieData = await dexieDB.getUserAnalysisData(userId, analysisUid) || {};
            await firebaseDB.updateMultipleNodes(userId, analysisUid, dexieAnalysis, dexieData);
            return;
        }

        if (!dexieAnalysis.deleted && firebaseAnalysis.deleted) {
            const firebaseData = await firebaseDB.getUserAnalysisData(userId, analysisUid) || {};
            await dexieDB.putUserAnalysis(userId, analysisUid, firebaseAnalysis, firebaseData);
            return;
        }

        const dexieDate = new Date(dexieAnalysis.lastUpdate || 0);
        const firebaseDate = new Date(firebaseAnalysis.lastUpdate || 0);

        if (dexieDate >= firebaseDate) {
            const dexieData = await dexieDB.getUserAnalysisData(userId, analysisUid) || {};
            await firebaseDB.updateMultipleNodes(userId, analysisUid, dexieAnalysis, dexieData);
        } else {
            const firebaseData = await firebaseDB.getUserAnalysisData(userId, analysisUid) || {};
            await dexieDB.putUserAnalysis(userId, analysisUid, firebaseAnalysis, firebaseData);
        }
    } finally {
        await dexieDB.setLastSyncDate(userId, new Date().toISOString());
        syncInProgress.delete(key);
    }
};